// "Nuevo cierre" — the manual pipeline's first step (B-40): pick a date and an office, and open
// the closing.
//
// The client is not picked here: it is the one the shell's header is on (`clientCode`, from
// `ClienteCierresContext`), shown fixed — asking for it again was a second choice of the same
// thing. `listCierreClients` is still read for its name and timezone, and `listCierreOffices` is
// that client's active offices, each flagged for the chosen date. Nothing here knows which client
// it is — a second client appears in the header by being registered.
//
// Opening a closing that already exists is not an error: `ensureCierreClosing` returns it with
// `created: false`, so this button is also how an operator gets to a closing the scheduler made.
// A day the office's calendar excludes warns and is still allowed — the backend records the
// exception on the closing's trace.
//
// B-60 — a client whose reports cover all its offices at once (Bancamía's three daily files) has
// no office to pick: the form shows `ProcesoDelDia` instead — one compact row per file, and "Procesar
// el día" in the footer. Which kind a client is comes from the backend, not from its name: the
// daily keys `getCierreDailyProgress` counts are the client's `scope: client` declarations, and a
// client with none keeps the office picker. If that answer fails, the office picker is what stays —
// the flow every client had before.
//
// Sending the day opens its progress in a second dialog (`AvanceDelDiaDialog`), which can be closed
// while the backend works. B-77 — the run is followed by `ClienteCierresProvider` (`diaEnCurso`),
// not here: the panels are remounted whenever a day is opened, so a run followed from this
// component was lost on the first click. Every `NuevoCierre` reads it — in the grouped list, in
// any day — and draws the same chip beside its button: "Procesando 12-10 · 40%" while it runs,
// also for a run another person started, and "Día procesado" once it ends, until the operator
// looks at the result or dismisses it. When it ends each mounted panel reloads (`onDiaProcesado`).
//
// BR-38 — and only what the person may do: the offices are those they may open a closing in
// (`cierres.closings.create`, asked of each office), and "Procesar el día" is offered to whoever
// holds `cierres.daily.run` on the whole client — the progress stays readable to everyone else.
//
// B-80 — sending a day that was already processed REDOES it: every office, closed ones included,
// starts again from its reports and what people did on them stops counting. So the send first asks
// (`ConfirmarRehacer`, fed by `previewCierreDayRedo`), and only then goes out with `redo`; a 409
// `DAY_ALREADY_PROCESSED` (processed by someone else meanwhile) asks the same way. Redoing needs
// `cierres.closings.reopen` on the whole client as well.
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Plus, Upload, X, XCircle } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import { ensureCierreClosing, listCierreClients, listCierreOffices, previewCierreDayRedo } from "../../../ApiCalls/mantisBusiness";
import { errorDeRehacer, resumenDelDia, seccionesDelDia } from "../../../utils/cierreRehacer";
import { ConfirmarRehacer } from "./ConfirmarRehacer";
import { correctionError } from "../../../utils/cierreCorrections";
import { hoyISO, opcionDeOficina } from "../../../utils/cierreManual";
import { SIN_OFICINAS_ABRIBLES, SIN_PROCESAR_DIA } from "../../../utils/accesoDiccionario";
import { PERMISO, puede } from "../../../utils/cierrePermisos";
import { useAccesoCierres } from "../../../hooks/useAccesoCierres";
import { useNombresCierre } from "../../../hooks/useNombresCierre";
import { AvanceDelDiaDialog } from "./AvanceDelDiaDialog";
import { ProcesoDelDia } from "./ProcesoDelDia";
import { useConciliacionDia } from "./useConciliacionDia";
import { useClienteCierres } from "./ClienteCierresContext";
import { fechaDMY } from "../../../utils/cierrePanel";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

// The panel's own inputs are `bg-card` with the theme's foreground; these match them rather than
// the primitive's hardcoded `bg-white`. (A date input's value being invisible was a different
// thing — a global rule in businessBackofficeStyles.scss; see brinksTheme.css.)
const CAMPO = "h-9 rounded-[8px] bg-card text-[12.5px] text-brinks-foreground";

// The form. Mounted only while the dialog is open, so the office, its date and an error start
// clean every time; the client, its daily state and a run being followed live in `NuevoCierre`.
// `fechaFija` — the form was opened for one day (that day's "Rehacer", "Procesar", a failed run's
// retry): the date is that day's and is not changed here.
function Formulario({ token, mantisBusinessId, clientes, clientCode, dia, nombres, esDiario, averiguando, fechaInicial, fechaFija = false, onOpened, onProcesar, onVerAvance, onCancelar }) {
  const [fecha, setFecha] = useState(fechaInicial || hoyISO());
  const [oficinas, setOficinas] = useState(null);
  const [officeCode, setOfficeCode] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const acceso = useAccesoCierres();

  useEffect(() => {
    if (!clientCode || !fecha || esDiario) return undefined;
    let activo = true;
    setOficinas(null);
    (async () => {
      const response = await listCierreOffices(token, mantisBusinessId, clientCode, fecha).catch(() => null);
      if (!activo) return;
      if (!response?.success) {
        setError(correctionError(response));
        setOficinas([]);
        return;
      }
      setError("");
      const lista = (response.data.offices || []).map(opcionDeOficina);
      setOficinas(lista);
      setOfficeCode((actual) => (lista.some((o) => o.valor === actual) ? actual : ""));
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, fecha, esDiario]);

  const oficina = (oficinas || []).find((o) => o.valor === officeCode);
  // The offices this person may open a closing in. The list the server sends is already only the
  // offices they may see; a role can see more than it may open.
  const abribles =
    oficinas === null
      ? null
      : oficinas.filter((o) => puede(acceso, PERMISO.CREAR_CIERRES, { cliente: clientCode, oficina: o.valor, region: o.region }));
  const ningunaAbrible = !!oficinas && oficinas.length > 0 && abribles.length === 0;
  // B-80 — a day already processed is redone, which also reopens and discards: reopening's permission too.
  const puedeProcesarDia =
    puede(acceso, PERMISO.PROCESAR_DIA, { cliente: clientCode, entero: true }) &&
    (!dia.procesado || puede(acceso, PERMISO.REABRIR, { cliente: clientCode, entero: true }));

  const abrir = async () => {
    setEnviando(true);
    setError("");
    const response = await ensureCierreClosing(token, mantisBusinessId, clientCode, officeCode, fecha).catch(() => null);
    setEnviando(false);
    if (!response?.success) {
      setError(correctionError(response));
      return;
    }
    // The region rides along for the manual dialog this opens, whose worksheet does not carry it.
    onOpened({ ...response.data, region: oficina?.region || null });
  };

  if (clientes === null) {
    return (
      <div className="grid place-items-center p-8">
        <Spinner />
      </div>
    );
  }

  const conocido = !!clientCode && !averiguando;
  const cliente = clientes.find((c) => c.code === clientCode);

  return (
    <>
      <div className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div {...guia("Cliente", "El cliente que tienes elegido en el encabezado. Para cambiarlo, cierra esta ventana y elígelo arriba.")} className="space-y-1.5">
            <Label id="nuevo-cierre-cliente-etiqueta" className="text-[12px] font-medium text-brinks-muted-foreground">
              Cliente
            </Label>
            {/* The client is the one the shell's header is on; it is changed there, not here — so the
                box reads as a disabled field, greyed, without a sentence saying so. */}
            <div
              aria-labelledby="nuevo-cierre-cliente-etiqueta"
              aria-disabled="true"
              className={cn(
                CAMPO,
                "flex cursor-not-allowed select-none items-center truncate border border-input bg-brinks-surface px-3 text-brinks-muted-foreground"
              )}
            >
              {clientCode ? cliente?.name || clientCode : "Sin clientes registrados"}
            </div>
          </div>

          <div {...guia("Fecha", "El día que vas a conciliar o del que abres el cierre. Por defecto es ayer.")} className="space-y-1.5">
            <Label htmlFor="nuevo-cierre-fecha" className="text-[12px] font-medium text-brinks-muted-foreground">
              {esDiario ? "Fecha a conciliar" : "Fecha del cierre"}
            </Label>
            <Input
              id="nuevo-cierre-fecha"
              type="date"
              className={CAMPO}
              value={esDiario ? dia.fecha : fecha}
              onChange={(e) => (esDiario ? dia.setFecha(e.target.value) : setFecha(e.target.value))}
              disabled={!conocido || dia.enviando || fechaFija}
              title={fechaFija ? "Se trabaja el día desde el que se abrió; para otro día, ábrelo desde esa fecha." : undefined}
            />
          </div>
        </div>

        {averiguando ? (
          <div className="grid place-items-center py-6">
            <Spinner className="h-5 w-5" />
          </div>
        ) : esDiario ? (
          dia.progreso ? (
            <ProcesoDelDia dia={dia} nombres={nombres} onVerAvance={onVerAvance} permitido={puedeProcesarDia} motivo={SIN_PROCESAR_DIA} />
          ) : dia.errorCarga ? (
            <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger">
              {dia.errorCarga}
            </p>
          ) : (
            <div className="grid place-items-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          )
        ) : (
          <div {...guia("Oficina", "La oficina cuyo cierre vas a abrir. Solo aparecen las oficinas en las que puedes abrir un cierre.")} className="space-y-1.5">
            <Label htmlFor="nuevo-cierre-oficina" className="text-[12px] font-medium text-brinks-muted-foreground">
              Oficina
            </Label>
            <Select value={officeCode} onValueChange={setOfficeCode} disabled={!clientCode || !abribles || abribles.length === 0}>
              <SelectTrigger id="nuevo-cierre-oficina" aria-label="Oficina" className={CAMPO}>
                <SelectValue
                  placeholder={
                    !clientCode
                      ? "Primero elige un cliente"
                      : oficinas === null
                        ? "Cargando oficinas…"
                        : oficinas.length === 0
                          ? "Sin oficinas activas"
                          : ningunaAbrible
                            ? "Sin oficinas que puedas abrir"
                            : "Elige una oficina"
                  }
                />
              </SelectTrigger>
              <SelectContent className="brinks-scope font-redhat">
                {(abribles || []).map((o) => (
                  <SelectItem key={o.valor} value={o.valor}>
                    {o.etiqueta}
                    {o.aviso ? " · no cierra ese día" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {ningunaAbrible ? (
              <p className="rounded-[8px] border border-brinks-border bg-brinks-surface px-2.5 py-1.5 text-[12.5px] text-brinks-muted-foreground">
                {SIN_OFICINAS_ABRIBLES}
              </p>
            ) : null}
            {oficina?.aviso ? (
              <p className="rounded-[8px] border border-brinks-warning-border bg-brinks-warning-soft px-2.5 py-1.5 text-[12.5px] text-brinks-warning">
                {oficina.aviso} Puedes abrirlo igual; la excepción queda registrada en el cierre.
              </p>
            ) : null}
          </div>
        )}

        {!esDiario && error ? (
          <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger">
            {error}
          </p>
        ) : null}
      </div>

      <DialogFooter className="sticky bottom-0 flex-row justify-end gap-2 border-t bg-inherit p-3 sm:space-x-0">
        <Button variant="outline" className="h-9" onClick={onCancelar}>
          Cancelar
        </Button>
        {esDiario ? (
          <Button {...guia("Procesar el día", "Envía los reportes y procesa todas las oficinas. El proceso sigue en el servidor aunque cierres esta ventana; el avance aparece junto a \"Nuevo cierre\".")} className="h-9 gap-1.5" disabled={!puedeProcesarDia || !dia.listoParaProcesar} onClick={onProcesar}>
            {dia.enviando ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
            {dia.enviando ? "Enviando…" : dia.procesado ? "Rehacer el día" : "Procesar el día"}
          </Button>
        ) : (
          <Button {...guia("Abrir cierre", "Abre el cierre de esa oficina en esa fecha. Si ya existe, abre el que hay.")} className="h-9 gap-1.5" disabled={!conocido || !officeCode || !fecha || enviando} onClick={abrir}>
            {enviando ? <Spinner className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            Abrir cierre
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

// The "Nuevo cierre" button, the chip that follows a day being processed, and both dialogs.
// B-70 — `pedido` (`{ fecha, clientCode }`, a new object each time) opens the form from outside,
// on that client and that date: the panel grouped by date's "Procesar día" on a day nobody processed.
// `clientCode` is the client the shell's header is on; the button opens the form on it.
export function NuevoCierre({ token, mantisBusinessId, clientCode: clienteDelEncabezado, onOpened, onDiaProcesado, pedido }) {
  const [formAbierto, setFormAbierto] = useState(false);
  // The date the form opens on when `pedido` asked for one; null when the button opened it.
  const [fechaPedida, setFechaPedida] = useState(null);
  // The same date for the office form, which keeps its own; it stays while that form is open.
  const [fechaDelFormulario, setFechaDelFormulario] = useState(null);
  const [avanceAbierto, setAvanceAbierto] = useState(false);
  const [clientes, setClientes] = useState(null);
  const [errorClientes, setErrorClientes] = useState("");
  const [clientCode, setClientCode] = useState("");

  // The client's day in progress, followed above the panels (B-77).
  const { diaEnCurso } = useClienteCierres();
  const { revisar, fin } = diaEnCurso;
  // A panel mounting asks once: a run started elsewhere shows here too.
  useEffect(() => {
    revisar();
  }, [revisar]);

  useEffect(() => {
    if (!formAbierto && !avanceAbierto) return undefined;
    let activo = true;
    (async () => {
      const response = await listCierreClients(token, mantisBusinessId).catch(() => null);
      if (!activo) return;
      if (!response?.success) {
        setErrorClientes(correctionError(response));
        setClientes([]);
        return;
      }
      setErrorClientes("");
      const lista = response.data.clients || [];
      setClientes(lista);
      setClientCode((actual) => (lista.some((c) => c.code === actual) ? actual : lista.length === 1 ? lista[0].code : ""));
    })();
    return () => {
      activo = false;
    };
  }, [formAbierto, avanceAbierto, token, mantisBusinessId]);

  const cliente = (clientes || []).find((c) => c.code === clientCode);
  const dia = useConciliacionDia({
    token,
    mantisBusinessId,
    clientCode,
    timezone: cliente?.timezone,
    listo: !!clientCode && (formAbierto || avanceAbierto),
  });
  const nombres = useNombresCierre(token, mantisBusinessId, clientCode);

  // Whether the chosen client has a daily process, remembered per client: changing the date clears
  // the progress for a moment, and the dialog must not flash the office picker while it reloads.
  // The answer is only taken from a progress that is this client's own.
  const [diarioPorCliente, setDiarioPorCliente] = useState({});
  useEffect(() => {
    const progreso = dia.progreso;
    if (!progreso || progreso.clientCode !== clientCode) return;
    const diario = Object.keys(progreso.documents || {}).length > 0;
    setDiarioPorCliente((actual) => (actual[clientCode] === diario ? actual : { ...actual, [clientCode]: diario }));
  }, [dia.progreso, clientCode]);
  const esDiario = diarioPorCliente[clientCode];
  const averiguando = !!clientCode && esDiario === undefined && !dia.errorCarga;

  // The panel reloads when the followed run ends, so the day's closings show up behind it.
  const finVisto = useRef(fin);
  useEffect(() => {
    if (fin === finVisto.current) return;
    finVisto.current = fin;
    if (onDiaProcesado) onDiaProcesado();
  }, [fin, onDiaProcesado]);

  useEffect(() => {
    if (!pedido) return;
    if (pedido.clientCode) setClientCode(pedido.clientCode);
    setFechaPedida(pedido.fecha || null);
    setFechaDelFormulario(pedido.fecha || null);
    // B-101 — "Rehacer el día" opens the same form: the day's files can be sent again there (or an
    // office's alone, which completes the day), and sending asks before anything is discarded.
    setFormAbierto(true);
  }, [pedido]);

  // The daily form's date lives in `useConciliacionDia`, which puts a client on its own yesterday
  // once it is ready — and again when the client's timezone arrives with the list. So the
  // asked-for date waits for the client itself, is applied after that, and is then let go.
  useEffect(() => {
    if (!fechaPedida || !(formAbierto || avanceAbierto) || !cliente || !dia.fecha) return;
    if (dia.fecha !== fechaPedida) dia.setFecha(fechaPedida);
    else setFechaPedida(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaPedida, formAbierto, avanceAbierto, cliente, dia.fecha]);

  // B-80 — the confirmation before redoing a processed day, and what it lists.
  const [confirmarDia, setConfirmarDia] = useState(false);
  const [previewDia, setPreviewDia] = useState(null);
  const [cargandoPreview, setCargandoPreview] = useState(false);
  const [errorPreview, setErrorPreview] = useState("");

  const abrirConfirmacion = async () => {
    setConfirmarDia(true);
    setPreviewDia(null);
    setErrorPreview("");
    setCargandoPreview(true);
    const response = await previewCierreDayRedo(token, mantisBusinessId, clientCode, dia.fecha).catch(() => null);
    setCargandoPreview(false);
    if (!response?.success) {
      setErrorPreview(errorDeRehacer(response));
      return;
    }
    setPreviewDia(response.data);
  };

  // The backend refused because the day was processed since the screen loaded: ask then.
  const { pideConfirmar, setPideConfirmar } = dia;
  useEffect(() => {
    if (!pideConfirmar) return;
    setPideConfirmar(false);
    abrirConfirmacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pideConfirmar]);

  const enviarDia = async (opciones) => {
    const seguir = await dia.procesar(opciones);
    if (!seguir) return false;
    diaEnCurso.seguir(dia.fecha);
    setFormAbierto(false);
    setAvanceAbierto(true);
    return true;
  };

  const procesar = async () => {
    if (dia.procesado) {
      abrirConfirmacion();
      return;
    }
    await enviarDia();
  };

  const rehacerDia = async () => {
    const enviado = await enviarDia({ redo: true });
    if (enviado) setConfirmarDia(false);
  };

  // From the form's own line: the day already on screen.
  const verAvance = () => {
    diaEnCurso.descartar();
    setFormAbierto(false);
    setAvanceAbierto(true);
  };

  // From the chip: the day in progress, which need not be the one the form was on.
  const verDiaEnCurso = () => {
    diaEnCurso.descartar();
    if (clienteDelEncabezado) setClientCode(clienteDelEncabezado);
    if (diaEnCurso.fecha && diaEnCurso.fecha !== dia.fecha) setFechaPedida(diaEnCurso.fecha);
    setFormAbierto(false);
    setAvanceAbierto(true);
  };

  // A run that stopped: the counters still show whatever the day had before, so they are not
  // drawn — the chip says it failed, with the backend's sentence, and offers to send the day again.
  const reintentar = () => {
    diaEnCurso.descartar();
    if (clienteDelEncabezado) setClientCode(clienteDelEncabezado);
    setFechaPedida(diaEnCurso.fecha || null);
    setFechaDelFormulario(diaEnCurso.fecha || null);
    setFormAbierto(true);
  };

  const { corriendo, terminado, avance, fallo } = diaEnCurso;
  const chip = !avanceAbierto && (corriendo || terminado);
  const fechaCorta = diaEnCurso.fecha ? fechaDMY(diaEnCurso.fecha).slice(0, 5) : "";
  const fallido = !corriendo && Boolean(fallo);
  const conAviso = !corriendo && !fallido && (avance.pendientes > 0 || avance.conProblema.length > 0);
  const bordeDelChip = fallido ? "border-brinks-danger-border" : conAviso ? "border-brinks-warning-border" : "border-brinks-success-border";

  return (
    // One box, so the chip always sits right beside the button: in a grid cell (PanelCierres) and
    // in a header that spreads its children apart (PanelCierresPorFecha) alike.
    <div {...guia("Nuevo cierre", "Abre el cierre de una oficina o procesa el día con los reportes que llegan del cliente.")} className="col-span-2 flex min-w-0 items-center gap-2 sm:col-span-1">
      <Button variant="outline" size="sm" className="h-9 flex-1 gap-1.5 sm:flex-none" onClick={() => {
          if (clienteDelEncabezado) setClientCode(clienteDelEncabezado);
          setFechaPedida(null);
          setFechaDelFormulario(null);
          setFormAbierto(true);
        }}>
        <Plus className="h-4 w-4 shrink-0" /> <span className="truncate">Nuevo cierre</span>
      </Button>

      {chip ? (
        <div
          role="status"
          className={cn(
            "flex h-9 min-w-0 max-w-[300px] items-center overflow-hidden whitespace-nowrap rounded-[8px] border text-[12px] font-medium",
            corriendo
              ? "border-brinks-border bg-card text-brinks-foreground"
              : fallido
                ? "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger"
                : conAviso
                  ? "border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning"
                  : "border-brinks-success-border bg-brinks-success-soft text-brinks-success"
          )}
        >
          <button
            type="button"
            onClick={fallido ? reintentar : verDiaEnCurso}
            title={fallido ? fallo : undefined}
            className="flex h-full min-w-0 flex-1 items-center gap-1.5 px-3 text-left"
          >
            {corriendo ? (
              <Spinner className="h-3.5 w-3.5 shrink-0" />
            ) : fallido ? (
              <XCircle className="h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            )}
            <span className="truncate">
              {corriendo
                ? `Procesando ${fechaCorta || "día"} · ${avance.porcentaje}%`
                : fallido
                ? `Falló el proceso del ${fechaCorta || "día"} · reintentar`
                : avance.pendientes
                  ? "Proceso incompleto · ver resultado"
                  : `Día procesado${avance.conProblema.length ? ` · ${avance.conProblema.length} con problema` : ""} · ver resultado`}
            </span>
          </button>
          {terminado ? (
            <button
              type="button"
              onClick={diaEnCurso.descartar}
              aria-label="Descartar aviso"
              className={cn("grid h-full w-8 shrink-0 place-items-center border-l opacity-70 hover:opacity-100", bordeDelChip)}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}

      <Dialog open={formAbierto} onOpenChange={setFormAbierto}>
        <DialogContent className="brinks-scope font-redhat flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-y-auto p-0">
          <DialogHeader className="border-b p-4 text-left">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] border bg-card text-brinks-foreground shadow-brinks-card">
                {esDiario ? <Upload className="h-[18px] w-[18px]" /> : <Plus className="h-[18px] w-[18px]" />}
              </span>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1">
                  <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">
                    {esDiario ? (dia.procesado ? "Rehacer el día" : "Procesar el día") : "Nuevo cierre"}
                  </DialogTitle>
                  <BotonGuia guiaId="dialogo-nuevo-cierre" titulo="Nuevo cierre o Procesar el día" texto="Para un cliente con proceso diario subes los reportes del día y se procesan todas sus oficinas. Para los demás, abres el cierre de una oficina en una fecha." nombre="Nuevo cierre" />
                </div>
                <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
                  {esDiario
                    ? dia.procesado
                      ? "Sube de nuevo los reportes que cambiaron; los que no envíes se toman de la carga anterior."
                      : `Sube los reportes del día y se procesan todas las oficinas de ${cliente?.name || clientCode}.`
                    : "Abre el cierre de una oficina para una fecha."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {formAbierto ? (
            <>
              {errorClientes ? (
                <p role="alert" className="m-4 mb-0 rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger">
                  {errorClientes}
                </p>
              ) : null}
              <Formulario
                token={token}
                mantisBusinessId={mantisBusinessId}
                clientes={clientes}
                clientCode={clientCode}
                dia={dia}
                nombres={nombres}
                esDiario={esDiario}
                averiguando={averiguando}
                fechaInicial={fechaDelFormulario}
                fechaFija={!!fechaDelFormulario}
                onOpened={(abierto) => {
                  setFormAbierto(false);
                  onOpened(abierto);
                }}
                onProcesar={procesar}
                onVerAvance={verAvance}
                onCancelar={() => setFormAbierto(false)}
              />
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmarRehacer
        open={confirmarDia}
        onOpenChange={setConfirmarDia}
        titulo="Rehacer el día"
        descripcion={previewDia ? resumenDelDia(previewDia) : "Este día ya se procesó: procesarlo de nuevo lo rehace."}
        cargando={cargandoPreview}
        errorCarga={errorPreview}
        aviso={previewDia && !previewDia.permitted ? "Tu perfil no permite rehacer este día: requiere reabrir cierres en todo el cliente." : ""}
        secciones={seccionesDelDia(previewDia, nombres, dia.archivos)}
        conMotivo={false}
        error={confirmarDia ? dia.errorEnvio : ""}
        enviando={dia.enviando}
        deshabilitado={!previewDia || !previewDia.permitted || previewDia.processed === false}
        etiquetaConfirmar="Rehacer el día"
        onConfirmar={rehacerDia}
      />

      <AvanceDelDiaDialog
        open={avanceAbierto}
        onOpenChange={setAvanceAbierto}
        dia={dia}
        nombres={nombres}
        cliente={cliente?.name || clientCode}
        token={token}
        mantisBusinessId={mantisBusinessId}
        clientCode={clientCode}
      />
    </div>
  );
}
